import { Link } from '@tanstack/react-router'
import { LegalPage } from '@/components/LegalPage'

/**
 * Terms of use for the Tech Inventory (issue #13).
 *
 * The theatre's website has its own terms, which are about reproducing content
 * from a public site; these are about a private system holding the charity's
 * asset records, so they are written fresh rather than copied. Governing law
 * follows the main site's: England and Wales.
 */
export function Terms() {
  return (
    <LegalPage title="Terms of use" updated="5 August 2026">
      <p>
        These terms cover the MADS Tech Inventory. By signing in you accept them. For the
        theatre&rsquo;s public website, see the{' '}
        <a href="https://madstheatre.org/terms-of-use/" target="_blank" rel="noreferrer">
          terms of use on madstheatre.org
        </a>
        .
      </p>

      <h2>Who provides this system</h2>
      <p>
        The MADS Tech Inventory is provided by MADS Theatre, a registered charity (number 512929) at
        Lord Street, Macclesfield, Cheshire, SK11 6SY.
      </p>

      <h2>Accounts</h2>
      <ul>
        <li>
          This is a private system for members of the theatre&rsquo;s technical team. Accounts are
          issued by an administrator; you cannot register for one, and you should not try to sign in
          without one.
        </li>
        <li>
          Your account is yours alone. Do not share your password, and do not let someone else work
          under your sign-in — the record of who changed what is only worth having if it is true.
        </li>
        <li>
          Signing in may take a second step — a passkey, or a code we text to your mobile. Keep the
          codes to yourself; nobody at the theatre will ever ask you for one. Only tick
          &ldquo;Trust this device&rdquo; on a device that is yours.
        </li>
        <li>
          Tell the Head of Tech promptly if you think someone else has got into your account, or if
          you no longer need it. If a trusted device has been lost, change your password &mdash;
          that ends the trust on every device at once.
        </li>
        <li>
          Accounts are deactivated when someone leaves the technical team, and may be suspended if
          these terms are broken.
        </li>
      </ul>

      <h2>Using it properly</h2>
      <ul>
        <li>
          Record equipment honestly and keep it up to date. Fault and PAT-testing records are relied
          on for safety, and a wrong entry is worse than no entry.
        </li>
        <li>
          Do not delete or alter records to hide damage, loss, or a test that was not carried out.
        </li>
        <li>
          Use what you find here for the theatre&rsquo;s purposes only. Contact details for other
          members are for theatre business, not for anything else.
        </li>
        <li>
          Do not attempt to reach parts of the system your account is not meant to reach, or to
          disrupt it for others.
        </li>
      </ul>

      <h2>The records themselves</h2>
      <p>
        Equipment records entered here belong to MADS Theatre and remain with the theatre when your
        account ends. The system, its design and its code likewise remain the theatre&rsquo;s.
        Personal data is handled as described in our <Link to="/privacy">privacy notice</Link>.
      </p>

      <h2>Availability</h2>
      <p>
        This is a volunteer-run system for a charity, provided as it is and without any promise that
        it will always be available or free of faults. It may be taken down for maintenance without
        notice. Keep a note elsewhere of anything you cannot afford to lose, and do not rely on it
        as the sole record for a legal or insurance purpose.
      </p>
      <p>
        Nothing in these terms limits liability for death or personal injury caused by negligence,
        for fraud, or for anything else that cannot be limited by law. Beyond that, the theatre is
        not liable for loss arising from your use of the system.
      </p>

      <h2>Changes</h2>
      <p>
        These terms may change; the date at the top shows when they last did, and continuing to use
        the system means accepting the current version.
      </p>

      <h2>Governing law</h2>
      <p>
        These terms, and any dispute arising from your use of this system, are governed by the laws
        of England and Wales and subject to the jurisdiction of its courts.
      </p>
    </LegalPage>
  )
}
